import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { apiUpload } from '../../../utils/fetch';
import { AdSpacePhotoInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import PhotoUploadSection from './PhotoUploadSection';

const initialPhoto: Partial<AdSpacePhotoInterface> = {
  adSpaceId: 0,
  photoUrl: '',
  active: true,
};

const AdSpacePhotosAdminAddPhotoPage: React.FC = () => {
  const [photo, setPhoto] = useState<Partial<AdSpacePhotoInterface>>(initialPhoto);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 5;

  const handleChange = (field: keyof AdSpacePhotoInterface, value: any) => {
    setPhoto(a => ({ ...a, [field]: value }));
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files
      .filter(file => file.size <= MAX_FILE_SIZE_MB * 1024 * 1024)
      .slice(0, MAX_FILES);

    if (files.length > MAX_FILES) {
      setError(`You can upload up to ${MAX_FILES} images.`);
    } else if (files.some(file => file.size > MAX_FILE_SIZE_MB * 1024 * 1024)) {
      setError(`Each image must be less than ${MAX_FILE_SIZE_MB}MB.`);
    } else {
      setError(null);
    }

    setUploadedFiles(validFiles);
    setPreviewUrls(validFiles.map(file => URL.createObjectURL(file)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!photo.adSpaceId || photo.adSpaceId === 0) {
      setError('Please enter an Ad Space ID');
      setDialogOpen(true);
      return;
    }

    if (uploadedFiles.length === 0) {
      setError('Please upload at least one photo');
      setDialogOpen(true);
      return;
    }

    try {
      const formData = new FormData();
      
      // Add adSpaceId to form data
      formData.append('adSpaceId', photo.adSpaceId.toString());
      formData.append('active', photo.active ? 'true' : 'false');

      // Add uploaded files (limit to 5)
      uploadedFiles.slice(0, 5).forEach((file) => {
        formData.append('uploadedFiles', file);
      });

      const result = await apiUpload<GenericResponseInterface<AdSpacePhotoInterface>>('/adSpacePhotos/insert', 'POST', formData);
      if (result.status) {
        setSuccess(result.message || 'Photo(s) created successfully!');
        setDialogOpen(true);
        setPhoto(initialPhoto);
        setUploadedFiles([]);
        setPreviewUrls([]);
        // Clean up object URLs
        previewUrls.forEach(url => URL.revokeObjectURL(url));
      } else {
        setError(result.message || 'Failed to create photo');
        setDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create photo');
      setDialogOpen(true);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add New Ad Space Photo</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
          label="Ad Space ID"
          type="number"
          value={photo.adSpaceId === 0 ? '' : photo.adSpaceId ?? ''}
          onChange={e => {
            const value = e.target.value === '' ? 0 : Number(e.target.value);
            handleChange('adSpaceId', value);
          }}
          fullWidth
          margin="dense"
          required
        />
        <PhotoUploadSection
          previewUrls={previewUrls}
          onPhotoUpload={handlePhotoUpload}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="active-label">Active</InputLabel>
          <Select
            labelId="active-label"
            value={photo.active !== undefined ? (photo.active ? 'yes' : 'no') : 'yes'}
            label="Active"
            onChange={e => handleChange('active', e.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create Photo
          </Button>
        </Box>
      </form>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{success || error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdSpacePhotosAdminAddPhotoPage;